import React, { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page } from "react-pdf";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useHistory } from "react-router-dom";
import { throttle } from "lodash";
import { TbArrowsSplit2 } from "react-icons/tb";
import PDFSplitter from "./PDFSplitter";
import { ReactComponent as ZoomInIcon } from "./Zoom In Bottom.svg";
import { ReactComponent as ZoomInIconHover } from "./Zoom In Button Hover.svg";
import { ReactComponent as ZoomOutIcon } from "./Zoom Out Bottom.svg";
import { ReactComponent as ZoomOutIconHover } from "./Zoom Out Button Hover.svg";
import { ReactComponent as PanToolIcon } from "./Pan Button.svg";
import { ReactComponent as PanToolIconHover } from "./Pan Button Hover.svg";
import { ReactComponent as CutIcon } from "./Scissors Botton.svg";
import { ReactComponent as CutIconHover } from "./Scissors Button Hover.svg";
import { ReactComponent as GrRotateRight } from "./Rotate Botton.svg";
import { ReactComponent as GrRotateRightHover } from "./Rotate Button Hover.svg";
import { ReactComponent as TrashIcon } from "./Trash Icon.svg";
import { ReactComponent as TrashIconHover } from "./Trash Icon Hover.svg";
import { axiosRequest } from "../axiosConfig";
import { isDemo } from "../App";

// Custom hook for handling pan behavior
const usePan = () => {
  const [isPanMode, setIsPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    if (!isPanMode) return;
    setIsPanning(true);
    setStartPoint({
      x: e.pageX - containerRef.current.offsetLeft,
      y: e.pageY - containerRef.current.offsetTop,
    });
    setScrollPos({
      x: containerRef.current.scrollLeft,
      y: containerRef.current.scrollTop,
    });
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;

    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const y = e.pageY - containerRef.current.offsetTop;

    const deltaX = x - startPoint.x;
    const deltaY = y - startPoint.y;

    containerRef.current.scrollLeft = scrollPos.x - deltaX;
    containerRef.current.scrollTop = scrollPos.y - deltaY;
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const togglePanMode = () => {
    setIsPanMode(!isPanMode);
  };

  return {
    containerRef,
    isPanning,
    isPanMode,
    togglePanMode,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp,
    },
  };
};

export default function PdfHighlighterPanZoom({
  pdfBlob,
  extractedData,
  hoveredFieldKey,
  pinnedHighlights,
  containerWidth = 750,
  containerHeight = 700,
  activePage,
  scrollOnPageChange,
  onScrolled,
  pdf,
  onRotationChange = () => {},
  setActivePage,
}) {
  const [numPages, setNumPages] = useState(null);
  const [pageBaseDims, setPageBaseDims] = useState({});
  const [highlights, setHighlights] = useState([]);
  const ZOOM_LEVEL = 1.25;
  const [zoom, setZoom] = useState(ZOOM_LEVEL);
  const [rotations, setRotations] = useState({});
  const pageRefs = useRef({});
  const [isZoomInHovered, setIsZoomInHovered] = useState(false);
  const [isZoomOutHovered, setIsZoomOutHovered] = useState(false);
  const [isRotateHovered, setIsRotateHovered] = useState(false);
  const [isPanHovered, setIsPanHovered] = useState(false);
  const [isCutHovered, setIsCutHovered] = useState(false);
  const [isTrashHovered, setIsTrashHovered] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [scrollLock, setScrollLock] = useState(false);
  const [lastFieldKey, setLastFieldKey] = useState(null);

  const history = useHistory();

  useEffect(() => {
    if (scrollLock || hoveredFieldKey === lastFieldKey) {
      return;
    }

    if (hoveredFieldKey && extractedData?.coordinates?.[hoveredFieldKey]) {
      const fieldInfo = extractedData.coordinates[hoveredFieldKey];
      const pageNum = fieldInfo.page;

      setScrollLock(true);
      setLastFieldKey(hoveredFieldKey);

      if (pageNum !== activePage) {
        setActivePage(pageNum);
        setTimeout(() => {
          if (pageRefs.current[pageNum]) {
            pageRefs.current[pageNum].scrollIntoView({
              behavior: "smooth",
              block: "start",
            });

            setTimeout(() => {
              const highlightElement = document.querySelector(
                `[data-highlight-id="${hoveredFieldKey}"]`
              );

              if (highlightElement) {
                highlightElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "center",
                });
              }

              setTimeout(() => {
                setScrollLock(false);
              }, 1000);
            }, 300);
          } else {
            setScrollLock(false);
          }
        }, 100);
      } else {

        const highlightElement = document.querySelector(
          `[data-highlight-id="${hoveredFieldKey}"]`
        );

        if (highlightElement) {
          highlightElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });
        }

        setTimeout(() => {
          setScrollLock(false);
        }, 1000);
      }
    }
  }, [
    hoveredFieldKey,
    extractedData,
    activePage,
    setActivePage,
    scrollLock,
    lastFieldKey,
  ]);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);

    const location = history.location;
    const routeState = location.state || {};

    let docInfo = pdf;
    let isChildDoc = false;

    if (routeState.isChildDocument && routeState.child) {
      docInfo = routeState.child;
      isChildDoc = true;
      console.log("Using child document info from router state:", docInfo);
    }

    if (!docInfo || !docInfo.Id) {
      console.error("Cannot delete: Missing document data", {
        pdf,
        routeState,
      });
      return;
    }

    console.log("Document info for deletion:", {
      docInfo,
      isChildDocument: isChildDoc,
      parentId: docInfo.parentId,
      id: docInfo.Id,
    });

    if (isChildDoc && docInfo.parentId) {
      console.log("Deleting child document with parent reference:", {
        childId: docInfo.Id,
        parentId: docInfo.parentId,
      });

      axiosRequest
        .delete(
          `${process.env.REACT_APP_BaseUrl}fax/childfax/${docInfo.parentId}/${docInfo.Id}`
        )
        .then((res) => {
          console.log("Child document deleted successfully");

          history.push({
            pathname: "/home",
            state: {
              userAdded: "true",
              message: "Document deleted successfully",
            },
          });
        })
        .catch((err) => {
          console.error("Child document deletion failed:", err);
        });
    } else {
      console.log("Deleting parent document:", {
        id: docInfo.Id,
      });

      axiosRequest
        .delete(`${process.env.REACT_APP_BaseUrl}fax/${docInfo.Id}`)
        .then((res) => {
          console.log("Parent document deleted successfully");

          history.push({
            pathname: "/home",
            state: {
              userAdded: "true",
              message: "Document deleted successfully",
            },
          });
        })
        .catch((err) => {
          console.error("Parent document deletion failed:", err);
        });
    }
  };

  // Initialize pan functionality
  const { containerRef, handlers, isPanning, isPanMode, togglePanMode } =
    usePan();

  const determineActivePage = useCallback(
    throttle(() => {
      if (!containerRef.current || Object.keys(pageRefs.current).length === 0)
        return;

      const container = containerRef.current;
      const containerBounds = container.getBoundingClientRect();
      const viewportTop = containerBounds.top;
      const viewportBottom = containerBounds.bottom;
      const viewportHeight = containerBounds.height;

      // Track which page has the most visible area
      let maxVisiblePage = activePage;
      let maxVisibleArea = 0;

      Object.entries(pageRefs.current).forEach(([pageNum, ref]) => {
        if (!ref) return;

        const pageBounds = ref.getBoundingClientRect();

        // Calculate how much of the page is visible
        const visibleTop = Math.max(pageBounds.top, viewportTop);
        const visibleBottom = Math.min(pageBounds.bottom, viewportBottom);

        // If page is visible at all
        if (visibleBottom > visibleTop) {
          const visibleArea = visibleBottom - visibleTop;
          // If this page has more visible area than previous maximum
          if (visibleArea > maxVisibleArea) {
            maxVisibleArea = visibleArea;
            maxVisiblePage = parseInt(pageNum, 10);
          }
        }
      });

      // Update active page if changed
      if (maxVisiblePage !== activePage) {
        setActivePage(maxVisiblePage);
      }
    }, 100),
    [activePage, setActivePage, containerRef]
  );

  // Add scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!scrollLock) {
        determineActivePage();
      }
    };

    console.log("PDF VIEW: ", pdf);

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, determineActivePage, scrollLock]);

  const rotateActivePage = () => {
    const page = activePage || 1;
    const newRotations = {
      ...rotations,
      [page]: ((rotations[page] || 0) + 90) % 360,
    };
    setRotations(newRotations);
    onRotationChange(newRotations);
  };

  function extractAllCoordinates(data) {
    if (!data?.coordinates) return [];
    return Object.entries(data.coordinates).map(([fieldKey, coordInfo]) => ({
      fieldKey,
      page: coordInfo.page,
      boundingBox: coordInfo.boundingBox,
    }));
  }

  useEffect(() => {
    if (extractedData) {
      const found = extractAllCoordinates(extractedData);
      setHighlights(found);
    }
  }, [extractedData]);

  function onDocumentLoadSuccess(pdf) {
    setNumPages(pdf.numPages);
  }

  function onPageRenderSuccess(page, pageNumber) {
    setPageBaseDims((prev) => ({
      ...prev,
      [pageNumber]: {
        width: page.width,
        height: page.height,
      },
    }));
  }

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 8.0));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 1.0));

  if (!pdfBlob) {
    return <div>No PDF provided</div>;
  }

  const scrollWithAnimation = (element, targetScrollTop, duration = 300) => {
    const start = element.scrollTop;
    const change = targetScrollTop - start;
    const startTime = performance.now();

    function animateScroll(currentTime) {
      const elapsedTime = currentTime - startTime;

      if (elapsedTime >= duration) {
        element.scrollTop = targetScrollTop;
        return;
      }

      // Easing function: easeInOutQuad
      const t = elapsedTime / duration;
      const factor = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

      element.scrollTop = start + change * factor;
      requestAnimationFrame(animateScroll);
    }

    requestAnimationFrame(animateScroll);
  };

  return (
    <Box sx={{ backgroundColor: "transparent" }}>
      <Paper
        elevation={0}
        sx={{
          mb: 0,
          p: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 2,
          justifyContent: "space-between",
          backgroundColor: "transparent",
        }}
      >
        <Box sx={{ width: 76 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "transparent",
          }}
        >
          <IconButton
            size="small"
            onClick={zoomOut}
            disabled={zoom <= 1.0}
            onMouseEnter={() => setIsZoomOutHovered(true)}
            onMouseLeave={() => setIsZoomOutHovered(false)}
            sx={{
              height: 30,
              width: 30,
              "&:hover": {
                backgroundColor: "rgba(72, 73, 232, 0.08)",
              },
            }}
          >
            {isZoomOutHovered ? (
              <ZoomOutIconHover fontSize="small" />
            ) : (
              <ZoomOutIcon fontSize="small" />
            )}
          </IconButton>

          <Select
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            size="small"
            displayEmpty
            renderValue={(value) => `${Math.round(value * 100)}%`}
            sx={{
              height: 30,
              minWidth: 80,
              fontSize: "0.875rem",
              color: "#9499A1",
              borderColor: "#DEE2E6",
              borderRadius: 2,
              borderWidth: 1.5,
              transition: "all 0.2s ease",
              "& .MuiSvgIcon-root": {
                color: "#9499A1",
                transition: "color 0.2s ease",
              },
              "&:hover": {
                "& .MuiSvgIcon-root": {
                  color: "#00AB4E",
                },
              },
              "&.Mui-focused": {
                "& .MuiSvgIcon-root": {
                  color: "#00AB4E",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#DEE2E6",
                borderRadius: 2,
                borderWidth: 1.5,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00AB4E",
                borderRadius: 2,
                borderWidth: 1.5,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00AB4E",
                borderRadius: 2,
                borderWidth: 1.5,
              },
            }}
          >
            <MenuItem value={1.0}>100%</MenuItem>
            <MenuItem value={1.25}>125%</MenuItem>
            <MenuItem value={1.5}>150%</MenuItem>
            <MenuItem value={1.75}>175%</MenuItem>
            <MenuItem value={2.0}>200%</MenuItem>
            <MenuItem value={2.25}>225%</MenuItem>
            <MenuItem value={2.5}>250%</MenuItem>
            <MenuItem value={2.75}>275%</MenuItem>
            <MenuItem value={3.0}>300%</MenuItem>
            <MenuItem value={3.25}>325%</MenuItem>
            <MenuItem value={3.5}>350%</MenuItem>
            <MenuItem value={3.75}>375%</MenuItem>
            <MenuItem value={4.0}>400%</MenuItem>
            <MenuItem value={4.25}>425%</MenuItem>
            <MenuItem value={4.5}>450%</MenuItem>
            <MenuItem value={4.75}>475%</MenuItem>
            <MenuItem value={5.0}>500%</MenuItem>
            <MenuItem value={5.25}>525%</MenuItem>
            <MenuItem value={5.5}>550%</MenuItem>
            <MenuItem value={5.75}>575%</MenuItem>
            <MenuItem value={6.0}>600%</MenuItem>
            <MenuItem value={6.25}>625%</MenuItem>
            <MenuItem value={6.5}>650%</MenuItem>
            <MenuItem value={6.75}>675%</MenuItem>
            <MenuItem value={7.0}>700%</MenuItem>
            <MenuItem value={7.25}>725%</MenuItem>
            <MenuItem value={7.5}>750%</MenuItem>
            <MenuItem value={7.75}>775%</MenuItem>
            <MenuItem value={8.0}>800%</MenuItem>
          </Select>

          <IconButton
            size="small"
            onClick={zoomIn}
            disabled={zoom >= 8.0}
            onMouseEnter={() => setIsZoomInHovered(true)}
            onMouseLeave={() => setIsZoomInHovered(false)}
            sx={{
              height: 30,
              width: 30,
              "&:hover": {
                backgroundColor: "rgba(72, 73, 232, 0.08)",
              },
            }}
          >
            {isZoomInHovered ? (
              <ZoomInIconHover fontSize="small" />
            ) : (
              <ZoomInIcon fontSize="small" />
            )}
          </IconButton>

          <IconButton
            size="small"
            onClick={rotateActivePage}
            onMouseEnter={() => setIsRotateHovered(true)}
            onMouseLeave={() => setIsRotateHovered(false)}
            sx={{
              height: 30,
              width: 30,
              "&:hover": {
                backgroundColor: "rgba(72, 73, 232, 0.08)",
              },
            }}
          >
            {isRotateHovered ? (
              <GrRotateRightHover fontSize="small" />
            ) : (
              <GrRotateRight fontSize="small" />
            )}
          </IconButton>

          <IconButton
            size="small"
            onClick={togglePanMode}
            onMouseEnter={() => setIsPanHovered(true)}
            onMouseLeave={() => setIsPanHovered(false)}
            sx={{
              height: 30,
              width: 30,
              backgroundColor: isPanMode
                ? "rgba(72, 73, 232, 0.08)"
                : "transparent",
              "&:hover": {
                backgroundColor: "rgba(72, 73, 232, 0.08)",
              },
            }}
          >
            {isPanMode ? (
              <PanToolIconHover fontSize="small" />
            ) : isPanHovered ? (
              <PanToolIconHover fontSize="small" />
            ) : (
              <PanToolIcon fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "transparent",
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              document.querySelector(".docbtn").click();
            }}
            onMouseEnter={() => setIsCutHovered(true)}
            onMouseLeave={() => setIsCutHovered(false)}
            sx={{
              height: 30,
              width: 30,
              "&:hover": {
                backgroundColor: "rgba(72, 73, 232, 0.08)",
              },
            }}
          >
            {isCutHovered ? (
              <CutIconHover fontSize="small" />
            ) : (
              <CutIcon fontSize="small" />
            )}
          </IconButton>

          <IconButton
            size="small"
            onClick={handleDeleteClick}
            onMouseEnter={() => setIsTrashHovered(true)}
            onMouseLeave={() => setIsTrashHovered(false)}
            sx={{
              height: 30,
              width: 30,
              "&:hover": {
                backgroundColor: "rgba(72, 73, 232, 0.08)",
              },
            }}
          >
            {isTrashHovered ? (
              <TrashIconHover fontSize="small" />
            ) : (
              <TrashIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Paper>

      <Box
        ref={containerRef}
        {...handlers}
        sx={{
          width: containerWidth,
          height: isDemo ?  '76vh'  :  '72vh',
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          position: "relative",
          bgcolor: "background.paper",
          cursor: isPanning ? "grabbing" : isPanMode ? "grab" : "default",
        }}
      >
        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">Loading PDF...</Typography>
            </Box>
          }
        >
          {Array.from(new Array(numPages), (el, i) => {
            const pageNum = i + 1;
            return (
              <PageMeasurer
                key={`page_meas_${pageNum}`}
                pageNum={pageNum}
                onPageRenderSuccess={onPageRenderSuccess}
              />
            );
          })}

          {Array.from(new Array(numPages), (el, i) => {
            const pageNum = i + 1;
            const baseDims = pageBaseDims[pageNum];
            if (!baseDims) return null;


            const rotation = rotations[pageNum] || 0;

            const isRotated = rotation % 180 !== 0;
            const pageWidth = isRotated ? baseDims.height : baseDims.width;
            const pageHeight = isRotated ? baseDims.width : baseDims.height;


            const scaledWidth = pageWidth * zoom;
            const scaledHeight = pageHeight * zoom;

            return (
              <Box
                key={`page_view_${pageNum}`}
                sx={{
                  position: "relative",
                  width: scaledWidth,
                  height: scaledHeight,
                  mb: 2.5,
                }}
                ref={(el) => {
                  pageRefs.current[pageNum] = el;

                  if (el && pageNum === activePage && scrollOnPageChange) {
                    el.scrollIntoView({
                      behavior: "smooth",
                      // block: "center",
                      inline: "center",
                    });
                    onScrolled();
                  }
                }}
              >
                <Box
                  sx={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "top left",
                    width: pageWidth,
                    height: pageHeight,
                    position: "relative",
                  }}
                >
                  <PageViewer
                    pageNum={pageNum}
                    width={pageWidth}
                    height={pageHeight}
                    rotation={rotation}
                  />
                  <HighlightOverlay
                    pageNum={pageNum}
                    highlights={highlights}
                    baseDims={baseDims}
                    hoveredFieldKey={hoveredFieldKey}
                    pinnedHighlights={pinnedHighlights}
                    rotation={rotation}
                  />
                </Box>
              </Box>
            );
          })}
        </Document>
      </Box>
      <PDFSplitter
        uploadedPdf={pdfBlob}
        totalPages={numPages}
        documentId={pdf?.Id}
        uploadEndpoint={`${process.env.REACT_APP_BaseUrl}splitpdf`}
        onSplitComplete={() => {
          console.log("Split complete");
        }}
        onClose={() => {
          // Handle cleanup
        }}
        disabled={!pdfBlob || numPages <= 1}
        className="hidden-btn"
      />
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle>
          Are you sure you want to delete this document?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This document will be deleted immediately. You can't undo this
            action.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: "#00AB4E",
              color: "#FFFFFF",
              fontFamily: "Manrope",
              "&:hover": {
                backgroundColor: "#009245",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PageMeasurer({ pageNum, onPageRenderSuccess }) {
  return (
    <div style={{ width: 0, height: 0 }}>
      <Page
        pageNumber={pageNum}
        scale={1}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        onRenderSuccess={(page) => onPageRenderSuccess(page, pageNum)}
      />
    </div>
  );
}

function PageViewer({ pageNum, width, height, rotation = 0 }) {
  return (
    <Page
      pageNumber={pageNum}
      scale={1}
      renderAnnotationLayer={false}
      renderTextLayer={false}
      width={width}
      style={{ position: "absolute", top: 0, left: 0 }}
      rotate={rotation}
    />
  );
}

function HighlightOverlay({
  pageNum,
  highlights,
  baseDims,
  hoveredFieldKey,
  pinnedHighlights,
  rotation = 0,
}) {
  const boxesForPage = highlights.filter((h) => h.page === pageNum);
  const { width: baseWidth, height: baseHeight } = baseDims;

  // Check if page is rotated
  const isRotated = rotation % 180 !== 0;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: baseDims.width,
        height: baseDims.height,
      }}
    >
      {boxesForPage.map((h, idx) => {
        const isSelected = h.fieldKey === hoveredFieldKey;
        const isPinned = pinnedHighlights.has(h.fieldKey);
        const shouldShow = isSelected || isPinned;

        if (!shouldShow) {
          return null;
        }

        let { Left, Top, Width, Height } = h.boundingBox;

        const pad = 0.01;
        Left -= pad / 2;
        Top -= pad / 2;
        Width += pad;
        Height += pad;

        if (Left < 0) {
          Width += Left;
          Left = 0;
        }
        if (Top < 0) {
          Height += Top;
          Top = 0;
        }
        if (Left + Width > 1) {
          Width = 1 - Left;
        }
        if (Top + Height > 1) {
          Height = 1 - Top;
        }

        // Calculate positions based on rotation
        let leftPx, topPx, widthPx, heightPx;

        // Normal positioning when not rotated
        if (rotation === 0) {
          leftPx = Left * baseWidth;
          topPx = Top * baseHeight;
          widthPx = Width * baseWidth;
          heightPx = Height * baseHeight;
        }
        // For 90° rotation
        else if (rotation === 90) {
          leftPx = (1 - Top - Height) * baseHeight;
          topPx = Left * baseWidth;
          widthPx = Height * baseHeight;
          heightPx = Width * baseWidth;
        }
        // For 180° rotation
        else if (rotation === 180) {
          leftPx = (1 - Left - Width) * baseWidth;
          topPx = (1 - Top - Height) * baseHeight;
          widthPx = Width * baseWidth;
          heightPx = Height * baseHeight;
        }
        // For 270° rotation
        else if (rotation === 270) {
          leftPx = Top * baseHeight;
          topPx = (1 - Left - Width) * baseWidth;
          widthPx = Height * baseHeight;
          heightPx = Width * baseWidth;
        }
        return (
          <div
            key={idx}
            data-highlight-id={h.fieldKey}
            style={{
              position: "absolute",
              left: leftPx,
              top: topPx,
              width: widthPx,
              height: heightPx,
              backgroundColor: "rgba(225, 225, 59, 0.3)",
              border: "2px solid #FCD34D",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </div>
  );
}
